// components/Screeners/LiveBreakoutTracker.jsx
import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FiRefreshCw } from "react-icons/fi";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import { useMarketSnapshot } from "../../context/MarketSnapshotContext";
import { useIndexSymbols } from "../../context/IndexSymbolContext";
import useLivePrices from "../../hooks/useLivePrices";
import { nseIndicesOptions } from "../Helper/Helperdata";
import { InfoTooltip } from "../Helper/InfoToolTip";
import { TOOLTIPS } from "../../helper/tooltilData";
import "./SmartMoneyScreener.css";

// Match the backend macdxover strategy so the frontend levels line up
const SL_CAP_PCT = 0.03;
const T1_R_MULT = 2;
const T2_R_MULT = 3;

const computeLevels = (crossoverHigh, crossoverLow) => {
  const high = Number(crossoverHigh);
  const low = Number(crossoverLow);
  if (!Number.isFinite(high) || !Number.isFinite(low)) return null;
  const sl = Math.max(low, high * (1 - SL_CAP_PCT));
  const r = high - sl;
  return {
    entry: Number(high.toFixed(2)),
    sl: Number(sl.toFixed(2)),
    t1: Number((high + T1_R_MULT * r).toFixed(2)),
    t2: Number((high + T2_R_MULT * r).toFixed(2)),
    r: Number(r.toFixed(2)),
  };
};

console.log("TOOLTIP DATA FILE V2 LOADED", {
  breakoutEntry: TOOLTIPS.breakoutEntry,
  breakoutPnlPoints: TOOLTIPS.breakoutPnlPoints,
  rMultiple: TOOLTIPS.rMultiple,
  breakoutStopLoss: TOOLTIPS.breakoutStopLoss,
  breakoutTarget1: TOOLTIPS.breakoutTarget1,
  breakoutTarget2: TOOLTIPS.breakoutTarget2,
});

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const t = new Date(dateStr).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
};

const LiveBreakoutTracker = () => {
  const navigate = useNavigate();
  const { allCrossoverData, allCrossoverLoading, loadAllCrossover } =
    useMarketSnapshot();
  const { prices: livePrices, subscribeSymbols } = useLivePrices();
  const { indexSymbols } = useIndexSymbols();

  const [selectedIndex, setSelectedIndex] = useState(
    () => localStorage.getItem("liveBreakoutIndex") || "ALL",
  );
  const [sortKey, setSortKey] = useState("crossover_date");
  const [sortDir, setSortDir] = useState("desc");

  // ── Load universe once (cached till EOD) ──
  useEffect(() => {
    loadAllCrossover();
  }, []);

  // ── Subscribe to live ticks for the current view ──
  useEffect(() => {
    if (!selectedIndex) return;
    localStorage.setItem("liveBreakoutIndex", selectedIndex);

    const symbols =
      selectedIndex === "ALL"
        ? (allCrossoverData?.data || []).map((r) => r.symbol)
        : indexSymbols?.[selectedIndex] || [];
    if (symbols.length) subscribeSymbols(symbols);
  }, [selectedIndex, allCrossoverData, indexSymbols]);

  // ── Build breakout rows: bullish + LTP > crossover_high ──
  const rows = useMemo(() => {
    const all = allCrossoverData?.data || [];
    const universe =
      !selectedIndex || selectedIndex === "ALL"
        ? all
        : all.filter((row) => row.indexes?.includes(selectedIndex));

    return universe
      .map((stock) => {
        const p = stock?.predictions?.daily;
        if (!p || p.macd_crossover !== "bullish") return null;

        const crossoverHigh = Number(p.crossover_high);
        const crossoverLow = Number(p.crossover_low);
        if (!Number.isFinite(crossoverHigh)) return null;

        const live = livePrices[stock.symbol];
        const dbLtp =
          stock?.quote?.data?.last_price ?? stock?.quote?.data?.ltp ?? null;
        const ltp = Number(live?.ltp ?? dbLtp);
        if (!Number.isFinite(ltp) || ltp <= 0) return null;

        // Only stocks that have already broken above the trigger
        if (ltp <= crossoverHigh) return null;

        const levels = computeLevels(crossoverHigh, crossoverLow);
        const pnlPoints = Number((ltp - crossoverHigh).toFixed(2));
        const pnlPct = Number(((pnlPoints / crossoverHigh) * 100).toFixed(2));
        const rMultiple =
          levels?.r > 0 ? Number((pnlPoints / levels.r).toFixed(2)) : null;

        return {
          symbol: stock.symbol,
          company: stock.company,
          security_id: stock.security_id,
          crossover_date: p.crossover_date,
          crossover_high: crossoverHigh,
          crossover_low: crossoverLow,
          ltp,
          isLive: !!live?.ltp,
          pnlPoints,
          pnlPct,
          rMultiple,
          sl: levels?.sl,
          t1: levels?.t1,
          t2: levels?.t2,
          rsi: p.rsi != null ? Number(p.rsi) : null,
          momentum: p.momentum,
          daysSince: daysSince(p.crossover_date),
        };
      })
      .filter(Boolean);
  }, [allCrossoverData, selectedIndex, livePrices]);

  // ── Sort ──
  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      if (sortKey === "symbol") {
        return sortDir === "asc"
          ? a.symbol.localeCompare(b.symbol)
          : b.symbol.localeCompare(a.symbol);
      }
      let aVal, bVal;
      if (sortKey === "crossover_date") {
        aVal = a.crossover_date ? new Date(a.crossover_date).getTime() : 0;
        bVal = b.crossover_date ? new Date(b.crossover_date).getTime() : 0;
      } else {
        aVal = a[sortKey] ?? -Infinity;
        bVal = b[sortKey] ?? -Infinity;
      }
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [rows, sortKey, sortDir]);

  // ── Summary bar totals ──
  const stats = useMemo(() => {
    if (!sorted.length) return null;
    const avgPnlPct = sorted.reduce((s, r) => s + r.pnlPct, 0) / sorted.length;
    const best = sorted.reduce(
      (max, r) => (r.pnlPct > max.pnlPct ? r : max),
      sorted[0],
    );
    const t1Hit = sorted.filter((r) => r.t1 && r.ltp >= r.t1).length;
    const t2Hit = sorted.filter((r) => r.t2 && r.ltp >= r.t2).length;
    return {
      count: sorted.length,
      avgPnlPct: avgPnlPct.toFixed(2),
      best,
      t1Hit,
      t2Hit,
    };
  }, [sorted]);

  const handleSort = useCallback(
    (key) => {
      if (sortKey === key) setSortDir(sortDir === "desc" ? "asc" : "desc");
      else {
        setSortKey(key);
        setSortDir("desc");
      }
    },
    [sortKey, sortDir],
  );

  //without tooltip
  // const SortTh = ({ label, field, align }) => (
  //   <th
  //     className={align === "left" ? "sm__th-left" : ""}
  //     onClick={() => handleSort(field)}
  //   >
  //     <div className="sm__sort-head">
  //       {label}
  //       <span className="sm__sort-arrows">
  //         <FaChevronUp
  //           size={10}
  //           className={sortKey === field && sortDir === "asc" ? "active" : ""}
  //         />
  //         <FaChevronDown
  //           size={10}
  //           className={sortKey === field && sortDir === "desc" ? "active" : ""}
  //         />
  //       </span>
  //     </div>
  //   </th>
  // );
  const SortTh = ({ label, field, align, tooltip, showSort = true }) => {
    const tooltipData = tooltip ? TOOLTIPS[tooltip] : null;

    return (
      <th className={align === "left" ? "sm__th-left" : ""}>
        <div className="sm__sort-head">
          <span>{label}</span>

          {tooltipData && <InfoTooltip text={tooltipData} />}

          {showSort && (
            <span className="sm__sort-arrows">
              <FaChevronUp
                size={10}
                className={
                  sortKey === field && sortDir === "asc" ? "active" : ""
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setSortKey(field);
                  setSortDir("asc");
                }}
              />

              <FaChevronDown
                size={10}
                className={
                  sortKey === field && sortDir === "desc" ? "active" : ""
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setSortKey(field);
                  setSortDir("desc");
                }}
              />
            </span>
          )}
        </div>
      </th>
    );
  };

  console.log({
    ltp: TOOLTIPS.ltp,
    entry: TOOLTIPS.breakoutEntry,
    pnl: TOOLTIPS.breakoutPnlPoints,
    r: TOOLTIPS.rMultiple,
    sl: TOOLTIPS.breakoutStopLoss,
    t1: TOOLTIPS.breakoutTarget1,
    t2: TOOLTIPS.breakoutTarget2,
  });
  return (
    <div className="sm">
      {/* Header */}
      <div className="sm__header">
        <div className="sm__header-left">
          <div className="sm__title-row">
            <button className="sm__back" onClick={() => navigate(-1)}>
              ←
            </button>
            <h1 className="sm__title">Live Breakout Tracker</h1>
          </div>
          <p className="sm__sub">
            Bullish daily crossovers with LTP above trigger — running P&L from
            crossover_high, latest first
          </p>
        </div>

        <div className="sm__header-right">
          <select
            className="sm__index-select"
            value={selectedIndex}
            onChange={(e) => setSelectedIndex(e.target.value)}
          >
            <option value="ALL">All ({allCrossoverData?.count || 0})</option>
            {nseIndicesOptions?.map((idx) => (
              <option key={idx} value={idx}>
                {idx}
              </option>
            ))}
          </select>
          <button
            className="sm__refresh-btn"
            onClick={() => loadAllCrossover(true)}
          >
            <FiRefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Summary */}
      {stats && (
        <div className="sm__result-bar" style={{ gap: 16 }}>
          <span>
            <b>{stats.count}</b> active breakouts
          </span>
          <span>
            Avg P&L:{" "}
            <b style={{ color: stats.avgPnlPct > 0 ? "#249966" : "#f87171" }}>
              {stats.avgPnlPct > 0 ? "+" : ""}
              {stats.avgPnlPct}%
            </b>
          </span>
          <span>
            T1 hit: <b>{stats.t1Hit}</b> · T2 hit: <b>{stats.t2Hit}</b>
          </span>
          <span>
            Best: <b>{stats.best.symbol}</b> (+{stats.best.pnlPct}%)
          </span>
        </div>
      )}

      {allCrossoverLoading && !sorted.length && (
        <div className="sm__loading">Loading crossover universe...</div>
      )}

      {!allCrossoverLoading && !sorted.length && (
        <div className="sm__empty">
          <div className="sm__empty-icon">📈</div>
          <div className="sm__empty-title">No live breakouts right now</div>
          <div className="sm__empty-sub">
            No bullish crossovers where LTP is above the trigger — check during
            market hours
          </div>
        </div>
      )}

      {sorted.length > 0 && (
        <div className="sm__table-wrap">
          <table className="sm__table">
            {/* without tooltip */}
            {/* <thead>
              <tr>
                <SortTh label="Symbol" field="symbol" align="left" />
                <SortTh label="Crossover Date" field="crossover_date" />
                <th>Age</th>
                <SortTh label="Entry" field="crossover_high" />
                <SortTh label="LTP" field="ltp" />
                <SortTh label="P&L ₹" field="pnlPoints" />
                <SortTh label="P&L %" field="pnlPct" />
                <SortTh label="R" field="rMultiple" />
                <SortTh label="Crossover_High" field="crossover_high" />
                <SortTh label="Crossover_Low" field="crossover_low" />
                <th>SL</th>
                <th>SL</th>
                <th>T1</th>
                <th>T2</th>
                <th>Status</th>
                <SortTh label="RSI" field="rsi" />
              </tr>
            </thead> */}
            <thead>
              <tr>
                <SortTh label="Symbol" field="symbol" align="left" />

                <SortTh label="Crossover Date" field="crossover_date" />

                <SortTh label="Age" field="daysSince" />

                <SortTh
                  label="Entry"
                  field="crossover_high"
                  tooltip="breakoutEntry"
                />

                <SortTh label="LTP" field="ltp" tooltip="ltp" />

                <SortTh
                  label="P&L/Share"
                  field="pnlPoints"
                  tooltip="breakoutPnlPoints"
                />

                <SortTh
                  label="P&L %"
                  field="pnlPct"
                  tooltip="breakoutPnlPercent"
                />

                <SortTh
                  label="Current R"
                  field="rMultiple"
                  tooltip="rMultiple"
                />

                <SortTh
                  label="Crossover High"
                  field="crossover_high"
                  tooltip="crossoverHigh"
                />

                <SortTh
                  label="Crossover Low"
                  field="crossover_low"
                  tooltip="crossoverLow"
                />

                <SortTh label="SL" field="sl" tooltip="breakoutStopLoss" />

                <SortTh label="T1 (2R)" field="t1" tooltip="breakoutTarget1" />

                <SortTh label="T2 (3R)" field="t2" tooltip="breakoutTarget2" />

                <SortTh
                  label="Status"
                  field="status"
                  tooltip="breakoutStatus"
                  showSort={false}
                />

                <SortTh label="RSI" field="rsi" tooltip="rsi" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => {
                const pnlColor = r.pnlPoints > 0 ? "#249966" : "#f87171";
                const t2Hit = r.t2 && r.ltp >= r.t2;
                const t1Hit = r.t1 && r.ltp >= r.t1;
                const status = t2Hit ? "🎯 T2" : t1Hit ? "🎯 T1" : "⏳ Running";
                const statusColor = t2Hit
                  ? "#249966"
                  : t1Hit
                    ? "#f59e0b"
                    : "#64748b";

                return (
                  <tr
                    key={r.symbol}
                    className="sm__row"
                    onClick={() =>
                      navigate(`/stockdetail/${r.symbol}/${r.security_id}`)
                    }
                  >
                    <td className="sm__td-name">
                      <div>
                        <span className="sm__symbol">{r.symbol}</span>
                        {r.company && (
                          <small
                            style={{
                              display: "block",
                              color: "#6b7280",
                              fontSize: "11px",
                            }}
                          >
                            {r.company}
                          </small>
                        )}
                      </div>
                    </td>

                    <td className="sm__td-num">{r.crossover_date || "—"}</td>
                    <td
                      className="sm__td-num"
                      style={{ color: "#6b7280", fontSize: "12px" }}
                    >
                      {r.daysSince != null ? `${r.daysSince}d` : "—"}
                    </td>
                    <td className="sm__td-num">
                      ₹{r.crossover_high.toFixed(2)}
                    </td>

                    <td className="sm__td-num" style={{ fontWeight: 700 }}>
                      ₹{r.ltp.toFixed(2)}
                      {r.isLive && (
                        <span
                          style={{
                            marginLeft: 4,
                            color: "#249966",
                            fontSize: 8,
                          }}
                          title="Live tick"
                        >
                          ●
                        </span>
                      )}
                    </td>

                    <td
                      className="sm__td-num"
                      style={{ color: pnlColor, fontWeight: 700 }}
                    >
                      {r.pnlPoints > 0 ? "+" : ""}₹{r.pnlPoints}
                    </td>
                    <td
                      className="sm__td-num"
                      style={{ color: pnlColor, fontWeight: 700 }}
                    >
                      {r.pnlPct > 0 ? "+" : ""}
                      {r.pnlPct}%
                    </td>
                    <td
                      className="sm__td-num"
                      style={{ color: pnlColor, fontWeight: 600 }}
                    >
                      {r.rMultiple != null ? `${r.rMultiple}R` : "—"}
                    </td>
                    <td className="sm__td-num" style={{ fontWeight: 600 }}>
                      {r.crossover_high ? r.crossover_high : "—"}
                    </td>
                    <td className="sm__td-num" style={{ fontWeight: 600 }}>
                      {r.crossover_low ? r.crossover_low : "—"}
                    </td>

                    <td className="sm__td-num" style={{ color: "#f87171" }}>
                      {r.sl ? `₹${r.sl}` : "—"}
                    </td>
                    <td className="sm__td-num" style={{ color: "#f59e0b" }}>
                      {r.t1 ? `₹${r.t1}` : "—"}
                    </td>
                    <td className="sm__td-num" style={{ color: "#249966" }}>
                      {r.t2 ? `₹${r.t2}` : "—"}
                    </td>

                    <td
                      className="sm__td-num"
                      style={{ color: statusColor, fontWeight: 600 }}
                    >
                      {status}
                    </td>

                    <td
                      className="sm__td-num"
                      style={{
                        color:
                          r.rsi > 55
                            ? "#249966"
                            : r.rsi < 45
                              ? "#f87171"
                              : "#64748b",
                        fontWeight: 600,
                      }}
                    >
                      {r.rsi != null ? r.rsi.toFixed(2) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LiveBreakoutTracker;
